/**
 * Builds the `defaults` payload for monday's create_column mutation.
 *
 * monday's `Column.settings` returns richer label objects than create_column
 * accepts: both schemas set `additionalProperties: false`, so any extra key
 * (status labels carry `hex`) is rejected. Status also requires `index` on every
 * label, which real boards omit. Passing settings straight through therefore
 * fails — see the status/dropdown schemas via get_column_type_info.
 *
 * Only status and dropdown take defaults. Returning null means "let monday use
 * its own labels" — that degrades the copy but still creates the column, which
 * beats failing creation outright over unreadable settings.
 */

type StatusLabel = {
  id?: number;
  label?: string;
  color?: string | number;
  index?: number;
  description?: string;
  is_done?: boolean;
  is_deactivated?: boolean;
};

type DropdownLabel = {
  id?: number;
  label?: string;
  is_deactivated?: boolean;
};

const withDefined = <T extends object>(entries: T): T =>
  Object.fromEntries(Object.entries(entries).filter(([, value]) => value !== undefined)) as T;

const statusLabel = (label: StatusLabel, position: number): StatusLabel =>
  withDefined({
    id: label.id,
    label: label.label,
    color: label.color,
    // `index` is required. Real boards omit it on some labels, so fall back to
    // the label's position in the array.
    index: label.index ?? position,
    description: label.description,
    is_done: label.is_done,
    is_deactivated: label.is_deactivated,
  });

const dropdownLabel = (label: DropdownLabel): DropdownLabel =>
  withDefined({
    id: label.id,
    label: label.label,
    is_deactivated: label.is_deactivated,
  });

export const buildColumnDefaults = (
  normalizedColumnType: string,
  labels: any,
): { settings: { labels: unknown[] } } | null => {
  if (normalizedColumnType !== "status" && normalizedColumnType !== "dropdown") return null;

  // monday returns labels as an array. Older payloads used an index->text object;
  // rather than guess at a conversion, fall back to monday's own labels.
  if (!Array.isArray(labels) || labels.length === 0) return null;

  const usable = labels.filter(
    (label) => label && typeof label.label === "string" && label.label !== "",
  );
  if (usable.length === 0) return null;

  return {
    settings: {
      labels:
        normalizedColumnType === "status" ? usable.map(statusLabel) : usable.map(dropdownLabel),
    },
  };
};
